import React, { useState, useEffect } from 'react';
import {
  Table, Card, Tag, Input, Select, Button, Space, message, Row, Col, Modal, Tabs,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, UserOutlined, SyncOutlined,
  LinkOutlined, BankOutlined,
} from '@ant-design/icons';
import api from '../utils/api';

const { Option } = Select;

const TASK_TEMPLATES = {
  ad_readonly: { label: '只读 (ANALYZE)', value: ['ANALYZE'] },
  ad_operate: { label: '投放 (ADVERTISE + ANALYZE)', value: ['ADVERTISE', 'ANALYZE'] },
  ad_manage: { label: '管理 (MANAGE + ADVERTISE + ANALYZE)', value: ['MANAGE', 'ADVERTISE', 'ANALYZE'] },
  page_operate: { label: '主页投放 (ADVERTISE + ANALYZE)', value: ['ADVERTISE', 'ANALYZE'] },
  page_manage: { label: '主页管理 (MANAGE + ADVERTISE + ANALYZE)', value: ['MANAGE', 'ADVERTISE', 'ANALYZE'] },
};

const showSyncFeedback = (data) => {
  if (!data) return;
  const ads = data.ad_accounts;
  const pages = data.pages;
  message.success(
    `同步完成：广告账户 ${ads?.linked ?? 0} 个，主页 ${pages?.linked ?? 0} 个`
  );
  if (data.warnings?.length) {
    data.warnings.forEach((text) => message.warning(text, 6));
  }
};

const FbUsers = () => {
  const [users, setUsers] = useState([]);
  const [bmList, setBmList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncLoadingId, setSyncLoadingId] = useState(null);
  const [filters, setFilters] = useState({
    bm_id: undefined,
    is_system_user: undefined,
    search: '',
  });

  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignType, setAssignType] = useState('pages');
  const [selectedUser, setSelectedUser] = useState(null);
  const [linkedAssets, setLinkedAssets] = useState(null);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [taskTemplate, setTaskTemplate] = useState('page_operate');

  const fetchBmList = async () => {
    try {
      const response = await api.get('/business-managers/');
      setBmList(response.data || []);
    } catch (error) {
      console.error('Fetch BM list error:', error);
    }
  };

  const fetchUsers = async (params = filters) => {
    setLoading(true);
    try {
      const query = { limit: 200 };
      if (params.bm_id) query.bm_id = params.bm_id;
      if (params.is_system_user !== undefined && params.is_system_user !== null) {
        query.is_system_user = params.is_system_user;
      }
      if (params.search?.trim()) query.search = params.search.trim();

      const response = await api.get('/business-managers/fb-users/list', { params: query });
      setUsers(response.data || []);
    } catch (error) {
      message.error('获取 FB 用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBmList();
    fetchUsers();
  }, []);

  const handleSearch = () => fetchUsers(filters);

  const handleReset = () => {
    const reset = { bm_id: undefined, is_system_user: undefined, search: '' };
    setFilters(reset);
    fetchUsers(reset);
  };

  const handleSyncInfo = async (record) => {
    setSyncLoadingId(record.id);
    try {
      const response = await api.post(`/business-managers/fb-users/${record.id}/sync-info`);
      showSyncFeedback(response.data);
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.detail || '同步失败');
    } finally {
      setSyncLoadingId(null);
    }
  };

  const loadLinkedAssets = async (record) => {
    setAssetsLoading(true);
    try {
      const response = await api.get(`/business-managers/fb-users/${record.id}/linked-assets`);
      setLinkedAssets(response.data);
    } catch (error) {
      message.error(error.response?.data?.detail || '获取资产失败');
      setLinkedAssets(null);
    } finally {
      setAssetsLoading(false);
    }
  };

  const openAssignModal = async (record, type) => {
    setSelectedUser(record);
    setAssignType(type);
    setSelectedRowKeys([]);
    setTaskTemplate(
      type === 'accounts'
        ? (record.is_system_user ? 'ad_manage' : 'ad_operate')
        : 'page_operate'
    );
    setAssignModalVisible(true);
    await loadLinkedAssets(record);
  };

  const handleAssign = async () => {
    if (!selectedUser || selectedRowKeys.length === 0) {
      message.warning('请选择要分配的资产');
      return;
    }

    const template = TASK_TEMPLATES[taskTemplate];
    if (!template) {
      message.warning('请选择权限模板');
      return;
    }

    setAssignLoading(true);
    try {
      const url =
        assignType === 'accounts'
          ? `/business-managers/fb-users/${selectedUser.id}/assign-ad-accounts`
          : `/business-managers/fb-users/${selectedUser.id}/assign-pages`;
      const payload =
        assignType === 'accounts'
          ? { ad_account_ids: selectedRowKeys, tasks: template.value }
          : { page_ids: selectedRowKeys, tasks: template.value };

      const response = await api.post(url, payload);
      const { assigned, errors } = response.data;
      if (assigned > 0) {
        message.success(`成功分配 ${assigned} 个${assignType === 'accounts' ? '广告账户' : '主页'}`);
      }
      if (errors?.length) {
        errors.forEach((err) => message.error(err, 8));
      }
      setSelectedRowKeys([]);
      await loadLinkedAssets(selectedUser);
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.detail || '分配失败');
    } finally {
      setAssignLoading(false);
    }
  };

  const adAccountColumns = [
    { title: '账户 ID', dataIndex: 'account_id', key: 'account_id' },
    { title: '名称', dataIndex: 'name', key: 'name' },
    {
      title: '权限',
      dataIndex: 'tasks',
      key: 'tasks',
      render: (tasks) =>
        tasks?.length ? tasks.map((t) => <Tag key={t}>{t}</Tag>) : '-',
    },
  ];

  const pageColumns = [
    { title: '主页 ID', dataIndex: 'page_id', key: 'page_id' },
    { title: '名称', dataIndex: 'name', key: 'name' },
    {
      title: '权限',
      dataIndex: 'tasks',
      key: 'tasks',
      render: (tasks) =>
        tasks?.length ? tasks.map((t) => <Tag key={t}>{t}</Tag>) : '-',
    },
  ];

  const unassignedData =
    assignType === 'accounts'
      ? linkedAssets?.unassigned_ad_accounts || []
      : linkedAssets?.unassigned_pages || [];

  const assignedData =
    assignType === 'accounts'
      ? linkedAssets?.ad_accounts || []
      : linkedAssets?.pages || [];

  const columns = [
    {
      title: 'Facebook 用户 ID',
      dataIndex: 'facebook_user_id',
      key: 'facebook_user_id',
      width: 180,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (v) => v || '-',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      render: (v) => v || '-',
    },
    {
      title: '用户类型',
      dataIndex: 'user_type',
      key: 'user_type',
      width: 120,
      render: (_, record) => {
        const type = record.user_type
          || (record.is_system_user ? 'system' : 'business');
        const labels = {
          system: { color: 'orange', text: '系统用户' },
          business: { color: 'blue', text: '普通用户' },
          partner: { color: 'default', text: 'Partner' },
        };
        const item = labels[type] || labels.business;
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    {
      title: '所属 BM',
      key: 'bm',
      render: (_, record) => (
        <div style={{ lineHeight: 1.5 }}>
          <div style={{ fontWeight: 500 }}>{record.bm_name || '-'}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{record.facebook_bm_id || '-'}</div>
        </div>
      ),
    },
    {
      title: '已分配',
      key: 'linked',
      width: 120,
      render: (_, record) => (
        <span>
          账户 {record.linked_ad_account_count || 0} / 主页 {record.linked_page_count || 0}
        </span>
      ),
    },
    {
      title: '最后同步',
      dataIndex: 'last_synced_at',
      key: 'last_synced_at',
      width: 170,
      render: (date) => (date ? new Date(date).toLocaleString() : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      fixed: 'right',
      render: (_, record) => (
        <Space wrap>
          <Button
            size="small"
            icon={<SyncOutlined />}
            loading={syncLoadingId === record.id}
            disabled={record.can_sync_info === false || record.user_type === 'partner'}
            onClick={() => handleSyncInfo(record)}
            title={
              record.can_sync_info === false || record.user_type === 'partner'
                ? 'Partner 用户无法通过 Business User API 同步信息，请使用分配主页/账户'
                : undefined
            }
          >
            {record.can_sync_info === false || record.user_type === 'partner'
              ? '无法同步信息'
              : '同步信息'}
          </Button>
          <Button
            size="small"
            icon={<LinkOutlined />}
            onClick={() => openAssignModal(record, 'pages')}
          >
            分配主页
          </Button>
          <Button
            size="small"
            icon={<BankOutlined />}
            onClick={() => openAssignModal(record, 'accounts')}
          >
            分配账户
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <UserOutlined />
            FB 用户管理
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              placeholder="筛选 BM"
              style={{ width: '100%' }}
              value={filters.bm_id}
              onChange={(value) => setFilters({ ...filters, bm_id: value })}
            >
              {bmList.map((bm) => (
                <Option key={bm.id} value={bm.id}>
                  {bm.name} ({bm.bm_id})
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              allowClear
              placeholder="用户类型"
              style={{ width: '100%' }}
              value={filters.is_system_user}
              onChange={(value) => setFilters({ ...filters, is_system_user: value })}
            >
              <Option value={false}>普通用户</Option>
              <Option value={true}>系统用户</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={7}>
            <Input
              placeholder="搜索名称 / 邮箱 / 用户 ID"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }}
        />
      </Card>

      <Modal
        title={
          selectedUser
            ? `${assignType === 'accounts' ? '分配账户' : '分配主页'} - ${selectedUser.name || selectedUser.facebook_user_id}`
            : '分配资产'
        }
        open={assignModalVisible}
        onCancel={() => {
          setAssignModalVisible(false);
          setSelectedUser(null);
          setLinkedAssets(null);
          setSelectedRowKeys([]);
        }}
        width={860}
        footer={[
          <Button key="cancel" onClick={() => setAssignModalVisible(false)}>
            关闭
          </Button>,
          <Button
            key="assign"
            type="primary"
            loading={assignLoading}
            disabled={selectedRowKeys.length === 0}
            onClick={handleAssign}
          >
            确认分配 ({selectedRowKeys.length})
          </Button>,
        ]}
      >
        <Space style={{ marginBottom: 16 }}>
          <span>权限模板：</span>
          <Select
            style={{ width: 280 }}
            value={taskTemplate}
            onChange={setTaskTemplate}
          >
            {Object.entries(TASK_TEMPLATES)
              .filter(([key]) =>
                assignType === 'accounts' ? key.startsWith('ad_') : key.startsWith('page_')
              )
              .map(([key, item]) => (
                <Option key={key} value={key}>
                  {item.label}
                </Option>
              ))}
          </Select>
        </Space>

        <Tabs
          items={[
            {
              key: 'unassigned',
              label: `未分配 (${unassignedData.length})`,
              children: (
                <>
                  <p style={{ marginBottom: 12, color: '#666', fontSize: 12 }}>
                    以下为该 BM 总部表中的资产，勾选后点击「确认分配」将调用 Meta API 授权给此用户
                  </p>
                  <Table
                    rowSelection={{
                      selectedRowKeys,
                      onChange: setSelectedRowKeys,
                    }}
                    columns={assignType === 'accounts' ? adAccountColumns : pageColumns}
                    dataSource={unassignedData}
                    rowKey="id"
                    loading={assetsLoading}
                    pagination={{ pageSize: 8 }}
                    size="small"
                    locale={{ emptyText: '暂无可分配资产，请先在 BM 管理同步资产' }}
                  />
                </>
              ),
            },
            {
              key: 'assigned',
              label: `已分配 (${assignedData.length})`,
              children: (
                <Table
                  columns={assignType === 'accounts' ? adAccountColumns : pageColumns}
                  dataSource={assignedData}
                  rowKey="id"
                  loading={assetsLoading}
                  pagination={{ pageSize: 8 }}
                  size="small"
                  locale={{ emptyText: '暂无已分配资产，可先同步信息或执行分配' }}
                />
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default FbUsers;
